import * as React from "react"

import { type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { useDecorativeSvgs } from "../../hooks/useDecorativeSvgs"
import { ChevronDownIcon } from "../ChevronDownIcon/ChevronDownIcon"
import { selectTriggerVariants } from "../Select/Select"

export type MenuTriggerProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> &
  VariantProps<typeof selectTriggerVariants> & {
    /** An avatar or icon drawn before the label. */
    leading?: React.ReactNode
    /**
     * The visible label, as text. The chevron is decorative and `leading` is
     * usually an image, so this text is what names the control when no
     * `aria-label` is given. A node type would admit `null` or an icon and
     * leave the button unnamed.
     */
    children: string
  }

/**
 * A button that opens a menu or popover, drawn exactly like `SelectTrigger`:
 * the same outline, surface, height, and chevron. Wrap it in `PopoverTrigger`
 * or `DropdownMenuTrigger` with `asChild`. The label ellipsizes rather than
 * wrapping, so a long name never breaks the control's fixed height.
 */
const MenuTrigger = React.forwardRef<HTMLButtonElement, MenuTriggerProps>(
  (
    { className, variant, leading, children, type = "button", ...props },
    ref,
  ) => {
    // The text label names the trigger, so an icon in `leading` is decoration.
    const setRef = useDecorativeSvgs(ref)
    return (
      <button
        ref={setRef}
        type={type}
        className={cn(selectTriggerVariants({ variant }), className)}
        {...props}
      >
        {leading}
        <span className="min-w-0 flex-1 truncate text-left">{children}</span>
        <ChevronDownIcon />
      </button>
    )
  },
)
MenuTrigger.displayName = "MenuTrigger"

export { MenuTrigger }
